import type { EnFormatPageProps } from "@/components/site/en/EnFormatPage";

export interface EnIndustryEntry {
  enPath: string;
  svPath: string;
  meta: { title: string; description: string };
  page: EnFormatPageProps;
}

export const EN_INDUSTRIES: Record<string, EnIndustryEntry> = {
  construction: {
    enPath: "/en/industries/construction",
    svPath: "/branscher/bygg",
    meta: {
      title: "Video for construction & infrastructure | Fenny",
      description:
        "Video production for construction, civil engineering and real estate developers. Project comms, recruitment, ESG and bid film — by a crew that's site-safety trained.",
    },
    page: {
      eyebrow: "Industry · Construction",
      heading: "Video for construction — by a crew that's allowed on site.",
      highlightIndices: [4, 5],
      intro:
        "Project communication, recruitment film and ESG documentation for construction and civil engineering — without slowing down production.",
      whatItIs: {
        title: "The library of film your projects deserve.",
        body: "Construction projects are long, visual and impossible to re-shoot. We capture them as they unfold — from groundwork to handover — and cut the footage into project comms, recruitment film, ESG reporting and bid material.",
      },
      whenToUse: [
        "Documenting a long-running build for client and stakeholder updates",
        "Recruiting tradespeople and engineers in a tight labour market",
        "ESG reporting to investors and procurement panels",
        "Bid film for the next tender or framework agreement",
      ],
      whatsIncluded: [
        "Site-safety trained crew (PPE, hot-work, ID06 equivalent)",
        "Drone and time-lapse capture of project progress",
        "Interviews with site management and trades",
        "2–3 minute project reel for site, sales and tender use",
        "30-second LinkedIn cutdowns",
        "Captions and platform-ready masters",
      ],
      outcomes: [
        { metric: "0", body: "production stops — we plan around your site schedule." },
        { metric: "12+ mo", body: "of usable footage from a single project on subscription." },
        { metric: "2–4×", body: "applicant volume on recruitment film vs. text ads." },
      ],
      faq: [
        {
          q: "Can you film on active construction sites?",
          a: "Yes. Our crew is trained in site safety, hot-work and lift work. We coordinate with site management and follow your safety protocols.",
        },
        {
          q: "How do you handle long projects?",
          a: "A monthly subscription captures the project from groundwork to handover. The footage builds into a library you can cut into project comms, recruitment and ESG material.",
        },
      ],
    },
  },
  ecommerce: {
    enPath: "/en/industries/ecommerce",
    svPath: "/branscher/e-handel",
    meta: {
      title: "Video for ecommerce & DTC brands | Fenny",
      description:
        "Product video, paid social creative and lifestyle film for ecommerce and DTC brands. Built to lift CTR, ROAS and AOV across Meta, TikTok and YouTube.",
    },
    page: {
      eyebrow: "Industry · Ecommerce",
      heading: "Video that earns the click and the second view.",
      highlightIndices: [2, 3],
      intro:
        "Product video, lifestyle film and paid-social creative for ecommerce and DTC brands — built around the metrics your media team actually reports on.",
      whatItIs: {
        title: "Creative that performs in paid media, not just brand decks.",
        body: "Ecommerce video has to earn the click and then earn the conversion. We script, shoot and edit with both jobs in mind — hero film for the PDP, hooks and cutdowns for paid social, and lifestyle stills from the same day.",
      },
      whenToUse: [
        "Launching a new product or category",
        "Refreshing tired paid-social creative",
        "Replacing static product images with motion on the PDP",
        "Building a UGC-style content flow for TikTok and Reels",
      ],
      whatsIncluded: [
        "Concept and script for hero film plus 6–10 cutdowns",
        "Studio or location shoot, 1–2 days",
        "Hooks tested for Meta, TikTok and YouTube Shorts",
        "Captioned 9:16, 1:1 and 16:9 masters",
        "Lifestyle stills from the same shoot day",
        "Performance-ready file delivery (under 4 MB for paid use)",
      ],
      outcomes: [
        { metric: "20–40%", body: "lift in CTR on paid social vs. static." },
        { metric: "1.5–2×", body: "ROAS on refreshed creative within 30 days." },
        { metric: "6+", body: "ad variants from a single shoot day." },
      ],
      faq: [
        {
          q: "Do you work with our existing media agency?",
          a: "Yes — we deliver platform-ready files and creative briefs that drop straight into your media team's workflow.",
        },
        {
          q: "Can you produce UGC-style content?",
          a: "Yes. We can either shoot UGC-style with creators or coach your own staff and customers on camera.",
        },
      ],
    },
  },
  "real-estate": {
    enPath: "/en/industries/real-estate",
    svPath: "/branscher/fastighet",
    meta: {
      title: "Video for real estate & property | Fenny",
      description:
        "Property film, project marketing and ESG reporting for real estate developers, landlords and brokers. Drone, walkthroughs and tenant stories.",
    },
    page: {
      eyebrow: "Industry · Real estate",
      heading: "Video for property — from groundbreaking to keys handed over.",
      highlightIndices: [3, 4],
      intro:
        "Project marketing, tenant stories and ESG film for developers, landlords and commercial brokers — produced as a steady flow, not one-off shoots.",
      whatItIs: {
        title: "The film layer your sales, leasing and reporting all share.",
        body: "Property is visual and slow-moving. We capture the journey from groundbreaking to handover and cut it into sales film for buyers, leasing reels for tenants and ESG content for boards and investors.",
      },
      whenToUse: [
        "Marketing a new development before completion",
        "Leasing commercial space to corporate tenants",
        "Reporting sustainability progress to investors",
        "Documenting refurbishments and tenant improvements",
      ],
      whatsIncluded: [
        "Drone and ground-based property footage",
        "Walkthrough films of units, common areas and amenities",
        "Tenant interviews and case stories",
        "Hero film for the project page plus social cutdowns",
        "ESG and progress reporting reels",
        "Captioned 9:16, 1:1 and 16:9 masters",
      ],
      outcomes: [
        { metric: "2–3×", body: "engagement on property pages with film vs. stills only." },
        { metric: "12+ mo", body: "footage library from one development on subscription." },
        { metric: "0", body: "extra effort from the leasing team — we plan around them." },
      ],
      faq: [
        {
          q: "Do you fly drones in built-up areas?",
          a: "Yes. We hold operator certification and handle permits and risk assessments for urban flights.",
        },
        {
          q: "Can you film tenants and residents?",
          a: "Yes. We coordinate consent, scheduling and on-camera coaching so tenants feel comfortable.",
        },
      ],
    },
  },
  health: {
    enPath: "/en/industries/health",
    svPath: "/branscher/halsa",
    meta: {
      title: "Video for healthcare & life sciences | Fenny",
      description:
        "Patient stories, recruitment film and explainer video for healthcare providers, life-science companies and medtech. Built around compliance and consent.",
    },
    page: {
      eyebrow: "Industry · Health & life sciences",
      heading: "Video for healthcare — built around consent, not around us.",
      highlightIndices: [3, 4],
      intro:
        "Patient stories, clinician recruitment, explainer film and investor updates for healthcare providers, medtech and life-science companies.",
      whatItIs: {
        title: "Film that respects patients, staff and regulators.",
        body: "Healthcare film lives or dies on trust. We work with clear consent forms, clinician sign-off and a crew used to ward and clinic environments — and we produce film that real patients, staff and investors actually want to watch.",
      },
      whenToUse: [
        "Recruiting clinicians and specialist nurses",
        "Explaining a treatment, device or care pathway",
        "Communicating change to staff across a hospital or region",
        "Investor and board updates for medtech and life-science companies",
      ],
      whatsIncluded: [
        "Crew briefed on infection control and clinical etiquette",
        "Consent process for patients and staff",
        "Clinician and patient interviews",
        "Treatment and device explainer film",
        "Hero film plus social cutdowns",
        "Captioned 9:16, 1:1 and 16:9 masters",
      ],
      outcomes: [
        { metric: "2–4×", body: "applicant volume on clinician recruitment film." },
        { metric: "30–50%", body: "lift in treatment-page engagement with explainer film." },
        { metric: "100%", body: "consent and approval coverage before publication." },
      ],
      faq: [
        {
          q: "Can you film inside hospitals and clinics?",
          a: "Yes. Our crew is briefed on infection control, clinical etiquette and consent. We coordinate with your communications and clinical leads in advance.",
        },
        {
          q: "How do you handle patient consent?",
          a: "Written, scoped consent forms covering territories, channels and time period. Patients can withdraw at any point before publication.",
        },
      ],
    },
  },
  industry: {
    enPath: "/en/industries/industry",
    svPath: "/branscher/industri",
    meta: {
      title: "Video for industrial & manufacturing companies | Fenny",
      description:
        "Factory tours, process explainers, recruitment film and B2B sales video for industrial and manufacturing companies. ATEX-aware crew, safety-trained.",
    },
    page: {
      eyebrow: "Industry · Manufacturing",
      heading: "Video for industry — by a crew that's been on a shop floor.",
      highlightIndices: [3, 4],
      intro:
        "Factory tours, process explainers, recruitment film and B2B sales video for industrial and manufacturing companies — produced without disrupting production.",
      whatItIs: {
        title: "Film that turns a complex process into a sales asset.",
        body: "Industrial buyers are technical and cautious. We translate complex manufacturing processes into clear film that engineers respect and procurement panels understand — and produce it on a schedule the plant manager actually agrees to.",
      },
      whenToUse: [
        "Selling into long B2B procurement cycles",
        "Recruiting operators, engineers and technicians",
        "Onboarding new staff and visiting customers",
        "Explaining a patented process or piece of equipment",
      ],
      whatsIncluded: [
        "Safety-trained crew (PPE, ATEX awareness, hot-work)",
        "Factory tour and process footage",
        "Engineer and operator interviews",
        "Hero film for sales and recruitment use",
        "Short cutdowns for LinkedIn and email outreach",
        "Captioned 9:16, 1:1 and 16:9 masters",
      ],
      outcomes: [
        { metric: "0", body: "production downtime — we plan around your shifts." },
        { metric: "2–3×", body: "reply rate on sales outreach with embedded film." },
        { metric: "12+ mo", body: "useful life on factory tour and process film." },
      ],
      faq: [
        {
          q: "Can you film in ATEX-classified zones?",
          a: "Yes. Our crew is briefed on ATEX and uses zone-appropriate equipment. We coordinate with your HSE lead in advance.",
        },
        {
          q: "Will filming slow down production?",
          a: "No. We plan around your shift patterns and shoot in parallel with normal operations.",
        },
      ],
    },
  },
  "public-sector": {
    enPath: "/en/industries/public-sector",
    svPath: "/branscher/offentlig-sektor",
    meta: {
      title: "Video for the public sector & government | Fenny",
      description:
        "Citizen information, recruitment film and internal communications for municipalities, agencies and public-sector employers. Procurement-friendly.",
    },
    page: {
      eyebrow: "Industry · Public sector",
      heading: "Video for the public sector — accessible, plain-spoken, on time.",
      highlightIndices: [4, 5],
      intro:
        "Citizen information film, recruitment campaigns and internal communications for municipalities, regions, agencies and public-sector employers.",
      whatItIs: {
        title: "Film that reaches every resident, not just the digital natives.",
        body: "Public-sector communication has to land with every resident, every employee and every stakeholder — regardless of language or accessibility need. We produce film with captions, plain language and translated versions built in from day one.",
      },
      whenToUse: [
        "Informing residents about a new service or change in policy",
        "Recruiting nurses, teachers, social workers and case officers",
        "Internal change communication across a large workforce",
        "Stakeholder reporting for boards and elected officials",
      ],
      whatsIncluded: [
        "Plain-language scripting and accessibility review",
        "Captions and audio description as standard",
        "Translated versions where needed",
        "Hero film plus short social cutdowns",
        "Procurement-friendly invoicing and documentation",
        "WCAG-compliant delivery files",
      ],
      outcomes: [
        { metric: "100%", body: "accessibility coverage (captions, audio description, plain language)." },
        { metric: "2–4×", body: "applicant volume on public-sector recruitment film." },
        { metric: "0", body: "procurement surprises — fixed scope and price." },
      ],
      faq: [
        {
          q: "Do you work within public procurement rules?",
          a: "Yes. We operate as a framework supplier where relevant and deliver against fixed scope, price and documentation requirements.",
        },
        {
          q: "Can you deliver in multiple languages?",
          a: "Yes. We produce translated voice-over, captions and on-screen text versions from the same shoot.",
        },
      ],
    },
  },
  restaurant: {
    enPath: "/en/industries/restaurant",
    svPath: "/branscher/restaurang",
    meta: {
      title: "Video for restaurants & hospitality | Fenny",
      description:
        "Food film, atmosphere reels and recruitment video for restaurants, hotels and hospitality groups. Built for Instagram, TikTok and booking platforms.",
    },
    page: {
      eyebrow: "Industry · Restaurants & hospitality",
      heading: "Video that makes guests book before they read the menu.",
      highlightIndices: [3, 4],
      intro:
        "Food film, atmosphere reels, chef stories and recruitment video for restaurants, hotels and hospitality groups — produced as an ongoing flow for social.",
      whatItIs: {
        title: "The reel that wins the table booking on Instagram.",
        body: "Hospitality runs on visuals. We capture food, atmosphere, chefs and service across a single shoot day and cut it into a month of reels, stories and booking-platform assets — without closing service.",
      },
      whenToUse: [
        "Launching a new menu, season or concept",
        "Filling tables on slow nights with paid social",
        "Recruiting chefs, baristas and front-of-house staff",
        "Selling private dining, events and group bookings",
      ],
      whatsIncluded: [
        "Single-day shoot covering food, atmosphere and team",
        "8–12 short reels for Instagram and TikTok",
        "Atmosphere reel for booking-platform listings",
        "Chef or owner story film",
        "Lifestyle stills from the same shoot",
        "Captioned 9:16 and 1:1 masters",
      ],
      outcomes: [
        { metric: "2–4×", body: "engagement on Reels with food film vs. stills." },
        { metric: "10–20%", body: "lift in midweek bookings on paid social with reels." },
        { metric: "1 day", body: "of shooting to feed a full month of social content." },
      ],
      faq: [
        {
          q: "Do you film during service?",
          a: "Yes. We plan around your busiest periods and shoot in parallel with normal service.",
        },
        {
          q: "Can you handle multiple venues in one group?",
          a: "Yes. We can run a touring shoot across a hospitality group and deliver a consistent visual identity for every venue.",
        },
      ],
    },
  },
  saas: {
    enPath: "/en/industries/saas",
    svPath: "/branscher/saas",
    meta: {
      title: "Video for SaaS & B2B software | Fenny",
      description:
        "Product demos, customer stories, founder film and explainer video for SaaS and B2B software companies. Built for landing pages, paid social and sales outreach.",
    },
    page: {
      eyebrow: "Industry · SaaS & B2B software",
      heading: "Video that shortens the SaaS sales cycle.",
      highlightIndices: [4, 5],
      intro:
        "Product demos, customer stories, founder film and explainer video for SaaS and B2B software companies — built around the metrics your growth team cares about.",
      whatItIs: {
        title: "The film layer your landing page, demo flow and sales outreach all share.",
        body: "SaaS buyers don't read — they scan, watch and forward. We produce a single library of product demos, customer stories and explainer film that drops into landing pages, sales decks and outbound emails.",
      },
      whenToUse: [
        "Launching a new product, feature or pricing tier",
        "Replacing a static demo with a guided video walkthrough",
        "Building customer story film for the website and sales team",
        "Refreshing paid-social creative on Meta and LinkedIn",
      ],
      whatsIncluded: [
        "Founder or product-lead interviews",
        "Screen-capture product demos with motion graphics",
        "Customer story film (remote or on-site)",
        "Hero film for the landing page plus social cutdowns",
        "Sales-team ready clips for outreach",
        "Captioned 9:16, 1:1 and 16:9 masters",
      ],
      outcomes: [
        { metric: "20–40%", body: "lift in landing-page conversion with product film." },
        { metric: "2–3×", body: "reply rate on outbound with embedded customer story." },
        { metric: "30–50%", body: "shorter sales cycles on deals exposed to demo film." },
      ],
      faq: [
        {
          q: "Can you film remote customer stories?",
          a: "Yes. We run a remote-but-broadcast-grade kit for international customer interviews and edit them in the same style as on-site shoots.",
        },
        {
          q: "Do you work with our product marketing team?",
          a: "Yes. We co-write scripts with product marketing and deliver files that drop into your existing CMS, paid-media and sales-enablement stack.",
        },
      ],
    },
  },
};
